/**
 * Cart helpers — client-side localStorage cart.
 * Price snapshot is for display; checkout recomputes server-side.
 */

export const CART_STORAGE_KEY = "floral-muse-cart";

export interface CartItem {
  productId: string;
  quantity: number;
  priceSnapshot: number; // pence, for display only
  title?: string;
  imageKey?: string;
  /**
   * Direct R2 URL for the product image/video. Stored so the cart can render
   * videos without routing them through the same-origin proxy (byte-range
   * handling is unreliable in Safari/Edge). Optional for backwards
   * compatibility with pre-existing cart entries in localStorage.
   */
  imageUrlRaw?: string;
  category?: string;
  /**
   * Snapshot of the product's `deliveryTiming` ("sameDayOk" | "advanceOnly" |
   * "enquireOnly"), used at checkout to decide the earliest preferred date:
   * pre-order ("advanceOnly") items need a lead time and can't be same-day.
   * Missing/undefined is treated conservatively as pre-order until the cart
   * revalidation backfills it from the server.
   */
  deliveryTiming?: string;
  /**
   * Selected product-variant id, when the product has variants and the
   * customer chose one. Absent for products without variants (or pre-existing
   * cart entries). `priceSnapshot` already reflects the chosen variant's price.
   */
  variantId?: string;
  /**
   * Human-readable variant label (e.g. "Deluxe") snapshotted at add-to-cart
   * time so the cart / order can display it without re-fetching the variant.
   */
  variantLabel?: string;
  /**
   * Snapshot of the product's add-on flag, so the cart can show an "Add-on"
   * label and warn that add-ons can't be ordered alone (re-validated server-side).
   */
  isAddon?: boolean;
}

/**
 * Stable per-line key. A product the customer added under two different
 * variants occupies two distinct cart lines, so the cart is keyed by
 * product + variant rather than `productId` alone. A product with no chosen
 * variant collapses to `"<productId>::"`.
 */
export function cartLineKey(item: {
  productId: string;
  variantId?: string;
}): string {
  return `${item.productId}::${item.variantId ?? ""}`;
}

export function addItem(
  items: CartItem[],
  item: Omit<CartItem, "quantity"> & { quantity?: number }
): CartItem[] {
  const qty = Math.max(1, Math.floor(item.quantity ?? 1));
  const key = cartLineKey(item);
  const existing = items.find((i) => cartLineKey(i) === key);
  const next: CartItem[] = existing
    ? items.map((i) =>
        cartLineKey(i) === key ? { ...i, quantity: i.quantity + qty } : i
      )
    : [...items, { ...item, quantity: qty }];
  return next;
}

/** Remove a single cart line by its `cartLineKey` (productId + variant). */
export function removeItem(items: CartItem[], lineKey: string): CartItem[] {
  return items.filter((i) => cartLineKey(i) !== lineKey);
}

export function updateQuantity(
  items: CartItem[],
  lineKey: string,
  quantity: number
): CartItem[] {
  const intQty = Math.floor(quantity);
  if (intQty <= 0) return removeItem(items, lineKey);
  return items.map((i) =>
    cartLineKey(i) === lineKey ? { ...i, quantity: intQty } : i
  );
}

export function getCartSubtotal(items: CartItem[]): number {
  return items.reduce((sum, i) => sum + i.priceSnapshot * i.quantity, 0);
}

export function clearCart(): CartItem[] {
  return [];
}
